import { c as _c } from "react/compiler-runtime";
import { Box, Flex, Text } from "@chakra-ui/react";
import { defineMessages, FormattedMessage } from "react-intl";
import React from "react";
export const ViewType = {
  trafficAnalysis: "trafficAnalysis",
  junctionAnalytics: "junctionAnalytics"
} as const;
export type ViewType = (typeof ViewType)[keyof typeof ViewType];
export const FilterType = {
  timesetPicker: "timesetPicker",
  vehicleTypes: "vehicleTypes",
  metrics: "metrics"
} as const;
export type FilterType = (typeof FilterType)[keyof typeof FilterType];
export interface MenuProps {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  activeFilter: FilterType | null;
  onFilterChange: (filter: FilterType) => void;
}
const messages = defineMessages({
  trafficAnalysis: {
    id: "menu.trafficAnalysis",
    defaultMessage: "Traffic Analysis"
  },
  junctionAnalytics: {
    id: "menu.junctionAnalytics",
    defaultMessage: "Junction Analytics"
  },
  timesetPicker: {
    id: "menu.timesetPicker",
    defaultMessage: "Timeset Picker"
  },
  vehicleTypes: {
    id: "menu.vehicleTypes",
    defaultMessage: "Vehicle Types"
  },
  metrics: {
    id: "menu.metrics",
    defaultMessage: "Metrics"
  }
});
export const filtersByView: Record<ViewType, FilterType[]> = {
  [ViewType.trafficAnalysis]: [FilterType.timesetPicker, FilterType.vehicleTypes, FilterType.metrics],
  [ViewType.junctionAnalytics]: [FilterType.timesetPicker, FilterType.vehicleTypes]
};
interface TabItemProps {
  isActive: boolean;
  onClick?: () => void;
  "data-testid": string;
  label: React.ReactNode;
}
const TabItem = t0 => {
  const $ = _c(9);
  const {
    isActive,
    onClick,
    "data-testid": testId,
    label
  } = t0;
  const t1 = isActive ? "black" : "grey.400";
  let t2;
  if ($[0] !== isActive || $[1] !== label) {
    t2 = <TabLabel isActive={isActive}>{label}</TabLabel>;
    $[0] = isActive;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isActive || $[4] !== onClick || $[5] !== t1 || $[6] !== t2 || $[7] !== testId) {
    t3 = <Box role="tab" aria-selected={isActive} borderTopWidth="tab" borderTopStyle="solid" borderTopColor={t1} px="8" py="3" cursor="pointer" onClick={onClick} data-testid={testId}>{t2}</Box>;
    $[3] = isActive;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
    $[7] = testId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
interface TabLabelProps {
  isActive: boolean;
  children: React.ReactNode;
}
const TabLabel = t0 => {
  const $ = _c(3);
  const {
    isActive,
    children
  } = t0;
  const t1 = isActive ? "bold" : "normal";
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <Text fontSize="sm" color="black" fontWeight={t1} textAlign="center">{children}</Text>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const Menu = t0 => {
  const $ = _c(16);
  const {
    activeView,
    onViewChange,
    activeFilter,
    onFilterChange
  } = t0;
  const filters = filtersByView[activeView];
  let t1;
  if ($[0] !== activeFilter || $[1] !== filters || $[2] !== onFilterChange) {
    let t2;
    if ($[4] !== activeFilter || $[5] !== onFilterChange) {
      t2 = filter => <TabItem key={filter} isActive={activeFilter === filter} onClick={() => onFilterChange(filter)} data-testid={`menu-filter-tab-${filter}`} label={<FormattedMessage {...messages[filter]} />} />;
      $[4] = activeFilter;
      $[5] = onFilterChange;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = filters.map(t2);
    $[0] = activeFilter;
    $[1] = filters;
    $[2] = onFilterChange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <Flex role="tablist" bg="cardBg" justify="center" data-testid="menu-filter-tabs">{t1}</Flex>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = Object.values(ViewType);
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== activeView || $[11] !== onViewChange) {
    t4 = <Flex role="tablist" bg="grey.200" justify="center" pb="12" data-testid="menu-view-tabs">{t3.map(view => <TabItem key={view} isActive={activeView === view} onClick={() => onViewChange(view)} data-testid={`menu-view-tab-${view}`} label={<FormattedMessage {...messages[view]} />} />)}</Flex>;
    $[10] = activeView;
    $[11] = onViewChange;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t4) {
    t5 = <Box data-testid="menu">{t2}{t4}</Box>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};